import apiClient from '../config/axios';

export interface Patient {
  user_id: string;
  name: string;
  phoneNumber: string;
  found?: boolean;
  created?: boolean;
}

export interface PatientSearchResponse {
  data: Array<{
    id: string;
    attributes: {
      user_id: string;
      name: string;
      phone_number: string;
    };
  }>;
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface PatientCreateResponse {
  data: {
    id: string;
    attributes: {
      user_id: string;
      name: string;
      phone_number: string;
      client: string;
    };
  };
}

export interface PatientCreateData {
  phoneNumber: string;
  name: string;
  weight_in_kg?: number;
  height_in_cms?: number;
  waist_circumference_in_cms?: number;
  gender?: string;
  place_of_birth?: string;
  time_of_birth?: string;
  age?: number;
  email?: string;
  current_location?: string;
  blood_group?: string;
  martial_status?: string;
  emergency_contact_number?: string;
  emergency_contact_name?: string;
}

class PatientService {
  /**
   * Search for a patient by phone number
   */
  static async searchByPhone(phone: string, page = 1, limit = 10): Promise<PatientSearchResponse> {
    try {
      const response = await apiClient.get('/patients/search', {
        params: {
          phone: phone,
          page: page,
          limit: limit
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error searching patient by phone:', error.message);
      throw new Error(`Failed to search patient by phone: ${error.message}`);
    }
  }

  /**
   * Search for a patient by patient ID
   */
  static async searchByPatientId(patientId: string, page = 1, limit = 10): Promise<PatientSearchResponse> {
    try {
      const response = await apiClient.get('/patients/search', {
        params: {
          patientId: patientId,
          page: page,
          limit: limit
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error searching patient by ID:', error.message);
      throw new Error(`Failed to search patient by ID: ${error.message}`);
    }
  }

  /**
   * Search for a patient by name
   */
  static async searchByName(name: string, page = 1, limit = 10, exact = false): Promise<PatientSearchResponse> {
    try {
      const response = await apiClient.get('/patients/search', {
        params: {
          name: name,
          page: page,
          limit: limit,
          exact: exact
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error searching patient by name:', error.message);
      throw new Error(`Failed to search patient by name: ${error.message}`);
    }
  }

  /**
   * Create a new patient
   */
  static async createPatient(patientData: PatientCreateData): Promise<PatientCreateResponse> {
    try {
      const response = await apiClient.post('/patients', patientData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error creating patient:', error.message);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw new Error(`Failed to create patient: ${error.message}`);
    }
  }

  /**
   * Find or create a patient based on phone number and name
   */
  static async findOrCreatePatient(patientData: PatientCreateData): Promise<Patient> {
    try {
      const response = await apiClient.post('/patients/find-or-create', patientData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = response.data.data;
      return {
        user_id: result.attributes.user_id,
        name: result.attributes.name,
        phoneNumber: result.attributes.phone_number,
        found: response.data.found || false,
        created: response.data.created || false
      };
    } catch (error: any) {
      console.error('Error in findOrCreatePatient:', error.message);
      throw error;
    }
  }
}

export default PatientService;
